import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ARTICLES_DATA } from "../../data/articlesData";

export function ArticlesSection() {
  const [isPaused, setIsPaused] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  const handleArticleClick = (slug) => {
    navigate(`/insights/${slug}`);
    window.scrollTo(0, 0);
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Business Strategy": "#dc2626",
      "Cost Optimization": "#16a34a",
      "Compliance": "#3b82f6",
      "IT Services": "#7c3aed",
      "Manpower": "#ea580c"
    };
    return colors[category] || "#64748b";
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-slate-50 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block font-bold text-xs tracking-[0.2em] uppercase text-red-600 mb-3 font-body"
          >
            Insights & Resources
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 mb-4"
          >
            Learn How to Scale Your Business
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 text-base max-w-2xl mx-auto font-body"
          >
            Expert tips, industry insights, and practical guides to help you make smarter vendor decisions
          </motion.p>
        </div>

        {/* Articles Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows - Desktop */}
          <button
            onClick={scrollLeft}
            className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-10 w-12 h-12 items-center justify-center rounded-full bg-white border-2 border-slate-200 shadow-lg hover:border-red-600 hover:text-red-600 transition-all"
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={scrollRight}
            className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-10 w-12 h-12 items-center justify-center rounded-full bg-white border-2 border-slate-200 shadow-lg hover:border-red-600 hover:text-red-600 transition-all"
            aria-label="Scroll right"
          >
            <ChevronRight size={24} />
          </button>

          {/* Scrollable Articles Container */}
          <div
            ref={scrollContainerRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="overflow-x-auto scrollbar-hide scroll-smooth"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <div className="flex gap-6 pb-4">
              {ARTICLES_DATA.map((article, index) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  index={index}
                  onClick={() => handleArticleClick(article.slug)}
                  getCategoryColor={getCategoryColor}
                />
              ))}
            </div>
          </div>

          {/* Gradient Overlays */}
          <div className="hidden lg:block absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-slate-50 to-transparent pointer-events-none z-5"></div>
          <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none z-5"></div>
        </div>

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <button
            onClick={() => {
              navigate("/insights");
              window.scrollTo(0, 0);
            }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white font-bold text-sm px-8 py-3 rounded-full shadow-lg shadow-red-600/30 hover:shadow-red-600/40 transition-all active:scale-95 font-body"
          >
            Explore All Articles
            <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function ArticleCard({ article, index, onClick, getCategoryColor }) {
  const [isHovered, setIsHovered] = useState(false);
  const categoryColor = getCategoryColor(article.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      className="flex-shrink-0 w-[320px] md:w-[360px] bg-white rounded-2xl overflow-hidden border border-slate-200 cursor-pointer transition-all duration-300 group"
      style={{
        boxShadow: isHovered ? `0 20px 40px ${categoryColor}20` : "0 4px 12px rgba(0,0,0,0.08)",
        transform: isHovered ? "translateY(-8px)" : "none",
      }}
    >
      {/* Article Image */}
      <div className="relative h-48 overflow-hidden bg-slate-100">
        <img
          src={article.image}
          alt={article.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Category Badge */}
        <div
          className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg backdrop-blur-sm font-body"
          style={{
            background: `${categoryColor}dd`,
          }}
        >
          {article.category}
        </div>
      </div>

      {/* Article Content */}
      <div className="p-6">
        {/* Title */}
        <h3 className="text-lg font-extrabold text-slate-900 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors font-body">
          {article.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-2 font-body">
          {article.description}
        </p>

        {/* Meta Info */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold font-body">
            <Clock size={14} />
            {article.readTime}
          </div>
          <div
            className={`flex items-center gap-1 text-sm font-bold transition-all ${
              isHovered ? "gap-2" : "gap-1"
            }`}
            style={{ color: categoryColor }}
          >
            Read More
            <ArrowRight size={16} className="transition-transform" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// CSS to hide scrollbar (add to index.css)
const scrollbarHideStyles = `
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
`;
